import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Manual backup card for the admin: shows record counts + a download button. */
export default function BackupPanel() {
  const [counts, setCounts] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const refresh = async () => {
    setErr("");
    try {
      const r = await api.get("/admin/backup/preview");
      setCounts(r.data.counts);
      setCheckedAt(r.data.checked_at);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const download = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const r = await api.get("/admin/backup/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try to read filename from Content-Disposition; fallback to date
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `grabarte_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setOk("✓ Copia de seguridad descargada. Guárdala en una carpeta segura.");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const total = counts ? Object.values(counts).reduce((a, b) => a + (b > 0 ? b : 0), 0) : null;

  return (
    <section className="mt-10 rounded-2xl border border-[#26775E]/30 bg-white p-5" data-testid="backup-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#26775E]">Copia de seguridad</p>
          <h2 className="mt-1 font-display text-xl">Descargar todos los datos</h2>
          <p className="mt-1 text-[13px] text-stone-600">
            Pulsa el botón para descargar un archivo .json con todos tus proyectos, biblioteca, convocatorias y usuarios.
            Guárdalo en una carpeta de tu Mac. <strong>Hazlo antes de cualquier redespliegue importante.</strong>
          </p>
        </div>
        <button onClick={download} disabled={busy} data-testid="backup-download-btn"
                className="rounded-full bg-[#26775E] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
          {busy ? "Descargando…" : "📥 Descargar copia ahora"}
        </button>
      </div>

      {counts && (
        <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Proyectos" value={counts.icaa_projects} />
          <Stat label="Usuarios" value={counts.users} />
          <Stat label="Biblioteca PDFs" value={counts.references} />
          <Stat label="Convocatorias" value={counts.saved_calls} />
          <Stat label="Cache funding" value={counts.funding_cache} />
          <Stat label="Jobs IA" value={counts.ai_jobs} />
        </div>
      )}

      {checkedAt && <p className="mt-2 text-[11px] text-stone-500">Datos vivos a {new Date(checkedAt).toLocaleString("es-ES")}. {total !== null && `Total: ${total} registros.`}</p>}

      {ok && <p className="mt-3 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="backup-ok">{ok}</p>}
      {err && <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="backup-error">{err}</p>}

      <p className="mt-4 text-[11px] italic text-stone-500">
        ℹ️ Las contraseñas <strong>NO</strong> se incluyen en la copia por seguridad. Si pierdes datos, contacta con el desarrollador para restaurar.
      </p>
    </section>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-2.5">
    <p className="text-[10px] uppercase tracking-widest text-stone-500">{label}</p>
    <p className="mt-0.5 font-display text-xl">{value === -1 ? "—" : value.toLocaleString()}</p>
  </div>
);
